import { useCallback, useEffect, useState } from 'react';
import { formatCurrency as money } from '../../lib/format';
import { Plus, Tag, AlertTriangle } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';
import ClinicDealModal from './ClinicDealModal';

interface DealProductRow {
  productId: string;
  productName: string;
  finalPrice: number;
  amCut: number;
  supervisorCut?: number;
  houseKeep: number;
  currentBasePrice: number | null;
}

interface DealRow {
  _id: string;
  name: string;
  products: DealProductRow[];
  startDate: string;
  endDate?: string;
  status: 'active' | 'ended' | 'cancelled';
  note?: string;
}

const statusStyles: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  ended: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-700'
};

function fmtDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Negotiated deals for ONE clinic, with admin add/end controls.
 * Rendered inside the clinic (physician) detail view.
 */
export default function ClinicDealsPanel({
  accountId,
  accountName,
  canManage
}: {
  accountId: string;
  accountName?: string;
  canManage: boolean;
}) {
  const [rows, setRows] = useState<DealRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiClient.getAccountClinicDeals(accountId);
    setLoading(false);
    if (res.success) setRows((res as any).deals || res.data?.deals || []);
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const handleEnd = async (id: string) => {
    setBusyId(id);
    const res = await apiClient.endClinicDeal(id);
    setBusyId(null);
    if (res.success) { toast.success('Deal ended'); load(); }
    else toast.error(res.error || 'Failed to end deal');
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">Negotiated deals</h3>
        </div>
        {canManage && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
          >
            <Plus className="h-4 w-4" /> New deal
          </button>
        )}
      </div>

      <p className="text-xs text-gray-500 mb-3">
        A deal overrides catalog pricing for this clinic — even when the catalog is cheaper — and
        pays the AM and supervisor the exact per-unit cuts below. Ending a deal is never
        retroactive; past orders keep their pricing.
      </p>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400">No deals on this clinic.</p>
      ) : (
        <div className="space-y-3">
          {rows.map(deal => (
            <div key={deal._id} className="border border-gray-200 rounded-md p-3">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{deal.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusStyles[deal.status]}`}>
                      {deal.status}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {fmtDate(deal.startDate)} → {deal.endDate ? fmtDate(deal.endDate) : 'open-ended'}
                  </div>
                </div>
                {canManage && deal.status === 'active' && (
                  <button
                    onClick={() => handleEnd(deal._id)}
                    disabled={busyId === deal._id}
                    className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {busyId === deal._id ? 'Ending…' : 'End deal'}
                  </button>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 text-left border-b border-gray-100">
                      <th className="py-1 pr-3 font-medium">Product</th>
                      <th className="py-1 px-2 font-medium text-right">Clinic pays</th>
                      <th className="py-1 px-2 font-medium text-right">Pharmacy</th>
                      <th className="py-1 px-2 font-medium text-right">House</th>
                      <th className="py-1 px-2 font-medium text-right">Supervisor</th>
                      <th className="py-1 pl-2 font-medium text-right">AM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deal.products.map(p => (
                      <tr key={p.productId} className="border-b border-gray-50 last:border-0">
                        <td className="py-1.5 pr-3 text-gray-900">{p.productName}</td>
                        <td className="py-1.5 px-2 text-right font-medium">{money(p.finalPrice)}</td>
                        <td className="py-1.5 px-2 text-right text-gray-600">
                          {p.currentBasePrice === null ? '—' : money(p.currentBasePrice)}
                        </td>
                        <td className={`py-1.5 px-2 text-right font-medium ${p.houseKeep < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                          {money(p.houseKeep)}
                        </td>
                        <td className="py-1.5 px-2 text-right text-gray-600">{money(p.supervisorCut || 0)}</td>
                        <td className="py-1.5 pl-2 text-right text-gray-600">{money(p.amCut)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pharmacy cost can rise after a deal is struck; deals are not
                  ratcheted like catalogs, so surface the inversion here. */}
              {deal.status === 'active' && deal.products.some(p => p.houseKeep < 0) && (
                <div className="mt-2 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-2 text-xs text-red-800">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    This deal now pays out more than it collects above pharmacy cost — the product's
                    base price rose after the deal was struck. Every order under it loses money.
                  </span>
                </div>
              )}

              {deal.note && <p className="text-xs text-gray-500 mt-2 italic">{deal.note}</p>}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <ClinicDealModal
          accountId={accountId}
          accountName={accountName}
          onClose={() => setShowModal(false)}
          onCreated={load}
        />
      )}
    </div>
  );
}
